import { useCallback, useEffect, useRef } from 'react';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import {
	ExperiencePerformanceTypes,
	ExperienceTypes,
	useUFOExperience,
} from '@atlassian/ufo-experimental';

import type { AudioFormat } from '../../api/cc-smarts/getAudio';
import type { AudioFeatureType } from '../../api/cc-smarts/models/AudioFeature';
import {
	type AudioPlayerStreamingAction,
	AudioPlayerStreamingManager,
} from '../effects/AudioPlayerStreamingManager';
import { getAIFeatureName } from '../effects/getAIFeatureName';
import { getRequestParamsForAudioPlayer } from '../effects/getRequestParamsForAudioPlayer';
import { audioPlayerState, getSupportingContent } from '../state/AudioPlayerState';
import { AudioStreamingState } from '../state/AudioPlayerStreamingStore';

import { isExpectedError } from './isExpectedError';
import { useAudioPlayerStreamingStore } from './useAudioPlayerStreamingStore';

type AudioPlayerStreamingConfig = {
	locale: string;
	format: AudioFormat;
	activationId: string;
	product: string;
	cloudId: string;
};

export function useAudioPlayerStreaming({
	activationId,
	product,
	cloudId,
	locale,
	format,
}: AudioPlayerStreamingConfig) {
	const audioRef = useRef<HTMLAudioElement>(null);
	const audioPlayerStreamingManager = useRef<AudioPlayerStreamingManager | null>(null);
	const featureOverrideRef = useRef<AudioFeatureType | null>(null);

	const { state, updateAudioPlayerStreamingStore } = useAudioPlayerStreamingStore();
	const { start, success, failure, abort } = useUFOExperience({
		id: 'audio-player-streaming',
		component: 'audio-player',
		type: ExperienceTypes.Experience,
		performanceType: ExperiencePerformanceTypes.InlineResult,
	});

	const { trackAIInteractionInit, trackAIResultView, trackAIResultError, updateAIAttributes } =
		useAIEventsInstrumentation();

	const handleObservabilityAndAnalytics = useCallback(
		async (action: AudioPlayerStreamingAction) => {
			// NOTE: we need to get the latest store update which might be delayed due to the async nature of the react render loop
			const featureToUse = featureOverrideRef.current ?? audioPlayerState.get().activeFeature;
			const { contentId, contentType } = audioPlayerState.get().activeContent;
			const contentAttributes = { contentId, contentType };

			switch (action.type) {
				case AudioStreamingState.LOADING:
					const featureName = getAIFeatureName(featureToUse);
					// Update AI analytics config
					updateAIAttributes({ aiFeatureName: featureName, ...contentAttributes });
					trackAIInteractionInit();
					await start();
					break;
				case AudioStreamingState.STREAM_STARTED:
					trackAIResultView();
					break;
				case AudioStreamingState.STREAM_DISPOSED:
					await success();
					break;
				case AudioStreamingState.ERROR:
					trackAIResultError({
						aiErrorMessage: action.error.message,
					});
					if (isExpectedError(action.error)) {
						await abort({
							metadata: {
								error: action.error.message,
							},
						});
						break;
					}
					await failure({
						metadata: {
							error: action.error.message,
						},
					});
					break;
			}
		},
		[
			trackAIInteractionInit,
			start,
			trackAIResultView,
			success,
			trackAIResultError,
			failure,
			abort,
			updateAIAttributes,
		],
	);

	const onAudioStreamingChange = useCallback(
		(action: AudioPlayerStreamingAction) => {
			updateAudioPlayerStreamingStore(action);
			handleObservabilityAndAnalytics(action);
		},
		[updateAudioPlayerStreamingStore, handleObservabilityAndAnalytics],
	);

	const fetchAudio = useCallback(
		async (featureOverride?: AudioFeatureType) => {
			featureOverrideRef.current = featureOverride ?? null;

			// Dispose previous player and stream if exists
			audioPlayerStreamingManager.current?.pauseAndResetPlayer();
			audioPlayerStreamingManager.current?.disposeStream();

			const requestParams = getRequestParamsForAudioPlayer({
				activationId,
				product,
				cloudId,
				locale,
				format,
				supportingContent: getSupportingContent(),
			});

			audioPlayerStreamingManager.current = new AudioPlayerStreamingManager({
				requestParams,
				onAudioStreamingChange,
			});

			audioPlayerStreamingManager.current.validateRequestParams();
			await audioPlayerStreamingManager.current.fetchAudio();
			audioPlayerStreamingManager.current.streamAudioToMediaSource(audioRef.current);
		},
		[activationId, product, cloudId, locale, format, onAudioStreamingChange],
	);

	/**
	 * Disposes streaming resources (reader, MediaSource) while preserving
	 * the audio element's playback position. Use this when hiding the UI
	 * but wanting to keep audio playback active.
	 *
	 * Note: This will stop buffering new data. If the stream hasn't fully
	 * loaded, playback will stop when the buffer runs out.
	 */
	const disposeStreamingResources = useCallback(() => {
		audioPlayerStreamingManager.current?.disposeStream();
	}, []);

	/**
	 * Indicates whether the audio stream has completed (either finished
	 * naturally or was disposed). When true, streaming resources have been
	 * freed and only the audio element's buffer remains.
	 */
	const isStreamComplete = state.state === AudioStreamingState.STREAM_DISPOSED;

	useEffect(() => {
		return () => {
			// eslint-disable-next-line react-hooks/exhaustive-deps
			audioPlayerStreamingManager.current?.disposeStream();
			audioPlayerStreamingManager.current?.resetState();
		};
	}, []);

	return { state, fetchAudio, audioRef, disposeStreamingResources, isStreamComplete };
}
